import _ from 'lodash';
import { Box, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { InspectorProvider } from '../../contexts/InspectorContext';
import ContentPanel from '../../components/shared/ContentPanel';
import EntryPanel from '../../components/shared/EntryPanel';
import TopicSearch from '../../components/shared/TopicSearch';
import EntrySearchInput from '../../components/shared/EntrySearchInput';
import { useEntrySearch } from '../../components/shared/useEntrySearch';
import useYourData from './YourDataTab/useYourData';
import yourDataEngine from './YourDataTab/YourDataEngine';
import YourDataPager from './YourDataTab/YourDataPager';
import userStateEngine2 from '../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../controllers/UserState/UserStateKeys';
import { useUser } from '../../contexts/UserContext'; // ADDED
import AppContext from '../../controllers/AppContext';
import { TabNames } from '../../services/wss/events/TabNames';
import './YourDataTab.css';

const loadLayout = () => {
    const sizes = localStorage.getItem('your-data-panels');
    return sizes ? JSON.parse(sizes) : [50, 50];
};

export default function YourDataTab({ sx }) {
    const { entries, selectedTopic, selectedEntry } = useYourData();
    const user = useUser(); // ADDED
    const { search, setSearch, filtered } = useEntrySearch(entries);

    // Hide completed toggle — local UI state, same pattern as YourNotesNodePager
    const [showCompleted, setShowCompleted] = useState(true);
    const completedCount = filtered.filter((e) => e.stateId === 1).length;
    const visibleEntries = showCompleted ? filtered : filtered.filter((e) => e.stateId !== 1);

    // Set active tab in AppContext so ContentPanel knows which tab is active // ADDED
    useEffect(() => {
        AppContext.setCurrentTab(TabNames.YourData);
    }, []);

    const handleLayoutChange = (sizes) => {
        localStorage.setItem('your-data-panels', JSON.stringify(sizes));
    };

    const handleTopicSelect = (name) => {
        userStateEngine2.set([
            { name: UserStateKeys.yourDataActiveTopic, value: name },
            { name: UserStateKeys.yourDataActiveEntry, value: null },
        ]);
        yourDataEngine.selectTopic(name);
    };

    const RECENT_OPTION = { name: '__recent__', total: null };
    const topics = [RECENT_OPTION, ..._.orderBy(user?.quota, ['name'], ['asc'])];

    return (
        <InspectorProvider>
            <Box className="your-data-root">
                <Box className="your-data-topic-bar">
                    <TopicSearch journals={topics} selectedName={selectedTopic} onSelect={handleTopicSelect} />
                </Box>

                <Group
                    id="entry-panel-group"
                    orientation="horizontal"
                    defaultLayout={loadLayout()}
                    onLayoutChange={handleLayoutChange}
                    style={{ flex: 1, minHeight: 0, width: '100%' }}
                >
                    <Panel id="entry-panel" defaultSize={25} minSize={15}>
                        <Box id="entry-panel-column" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            {/* Search — fixed, never scrolls */}
                            <Box
                                id="entry-panel-search-row"
                                px={1}
                                py={0.75}
                                sx={{
                                    // borderBottom: '0.5px solid #294766',
                                    bgcolor: 'var(--j-canvas)',
                                    flexShrink: 0,
                                }}
                            >
                                <EntrySearchInput value={search} onChange={setSearch} />
                            </Box>

                            {/* Info panel — fixed, never scrolls. Completed checkbox + count left, pager right */}
                            <Box
                                id="entry-panel-info-row"
                                sx={{
                                    px: '1px',
                                    py: 0.75,
                                    mx: 1,
                                    mb: 1,
                                    border: '0.5px solid #294766',
                                    bgcolor: 'var(--j-canvas)',
                                    flexShrink: 0,
                                }}
                            >
                                {/* Inner wrapper owns the flex row — outer Box owns the padding, same layering as search box above */}
                                <Box
                                    id="entry-panel-info-row-flex"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                    }}
                                >
                                    {/* Left — hide completed checkbox + total count, only when relevant. Styling matches YourNotesNodePager for now */}
                                    <Box
                                        id="entry-panel-completed-toggle"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                                    >
                                        {completedCount > 0 && (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={showCompleted}
                                                        onChange={(e) => setShowCompleted(e.target.checked)}
                                                        sx={{
                                                            color: '#a3d1ff',
                                                            '&.Mui-checked': { color: '#a3d1ff' },
                                                            padding: '2px 4px',
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" sx={{ color: '#a3d1ff', fontSize: '11px' }}>
                                                        Completed ({completedCount})
                                                    </Typography>
                                                }
                                                sx={{ margin: 0 }}
                                            />
                                        )}

                                        <Typography
                                            id="entry-panel-total-count"
                                            variant="caption"
                                            sx={{ pl: '4px', color: '#a3d1ff', fontSize: 12, flexShrink: 0 }}
                                        >
                                            {visibleEntries.length} entries
                                            {!showCompleted && completedCount > 0 && (
                                                <span style={{ color: '#6b8fa8', marginLeft: 4 }}>({completedCount} hidden)</span>
                                            )}
                                        </Typography>
                                    </Box>

                                    {/* Right — prev/next pager across server-side pages of the active topic */}
                                    <YourDataPager />
                                </Box>
                            </Box>

                            <Divider sx={{ borderBottom: '0.5px solid #294766' }} />

                            {/* List — only this scrolls */}
                            <Box id="entry-panel-scroll-area" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                <EntryPanel entries={visibleEntries} selectedEntry={selectedEntry} onSelect={yourDataEngine.selectEntry} />
                            </Box>
                        </Box>
                    </Panel>

                    <Separator className="j-splitter" />

                    <Panel id="content-panel" minSize={30}>
                        <ContentPanel entry={selectedEntry} />
                    </Panel>
                </Group>
            </Box>
        </InspectorProvider>
    );
}
